/**
 * Admin / Instructor / SME — Assignment Grading dashboard
 *
 * Lists every assignment submission, filterable by program + chapter + status.
 * Admins see everything; instructors and SMEs see only submissions on
 * programs they own.
 *
 * Clicking Grade opens a dialog with the student's file download link, marks
 * input (capped at the assignment's max_marks) and a feedback textarea.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    ClipboardList, Search, Loader2, Download, FileText,
    CheckCircle2, ChevronRight, Star,
} from "lucide-react";

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium", timeStyle: "short",
        });
    } catch { return iso; }
}

const STATUS_STYLES = {
    submitted: "bg-amber-100 text-amber-900 border-amber-200",
    graded: "bg-emerald-100 text-emerald-900 border-emerald-200",
    locked: "bg-slate-100 text-slate-700 border-slate-200",
    unlocked: "bg-blue-100 text-blue-900 border-blue-200",
};

export default function AssignmentGrading() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("submitted");
    const [q, setQ] = useState("");
    const [grading, setGrading] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter && statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/admin/assignment-submissions", { params });
            setItems(data.items || []);
        } catch (e) {
            toast.error(fmtError(e, "Could not load submissions"));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const needle = q.trim().toLowerCase();
        return items.filter((s) =>
            (s.student?.email || "").toLowerCase().includes(needle)
            || (s.student?.name || "").toLowerCase().includes(needle)
            || (s.student?.student_id || "").toLowerCase().includes(needle)
            || (s.chapter?.title || "").toLowerCase().includes(needle),
        );
    }, [items, q]);

    const stats = useMemo(() => {
        return {
            submitted: items.filter((s) => s.status === "submitted").length,
            graded: items.filter((s) => s.status === "graded").length,
            total: items.length,
        };
    }, [items]);

    const downloadSubmission = async (sub) => {
        try {
            const res = await api.get(
                `/admin/assignment-submissions/${sub.id}/download`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = sub.file?.filename || `submission-${sub.id}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(fmtError(e, "Could not download submission"));
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 pb-24" data-testid="admin-assignment-grading">
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">← Back</Link>
            <div className="mt-2 mb-8">
                <div className="inline-flex items-center gap-1.5 chip chip-primary">
                    <ClipboardList className="h-3 w-3" />
                    Grading
                </div>
                <h1 className="font-display text-3xl md:text-4xl text-foreground mt-3 tracking-tight">
                    Assignment submissions
                </h1>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                    Review student assignment submissions and award marks. Admins see
                    every submission; instructors and SMEs see only their own programs.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <Card className="p-3 bg-[#FBF5E8] border-[#E1DAC5]">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Awaiting grade
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">
                        {stats.submitted}
                    </div>
                </Card>
                <Card className="p-3 bg-emerald-50 border-emerald-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Graded
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">
                        {stats.graded}
                    </div>
                </Card>
                <Card className="p-3 bg-slate-50 border-slate-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Total visible
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">
                        {stats.total}
                    </div>
                </Card>
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, email, HCCD, or chapter…"
                        className="pl-9"
                        data-testid="grading-search"
                    />
                </div>
                <div className="inline-flex items-center gap-1 rounded-md bg-muted/50 p-1">
                    {[
                        { v: "submitted", label: "Awaiting grade" },
                        { v: "graded", label: "Graded" },
                        { v: "all", label: "All" },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => setStatusFilter(opt.v)}
                            className={`px-3 py-1 text-xs rounded ${statusFilter === opt.v ? "bg-[#134B30] text-white" : "text-muted-foreground hover:text-foreground"}`}
                            data-testid={`grading-filter-${opt.v}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-12 flex items-center justify-center text-muted-foreground">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-16 text-center text-sm text-muted-foreground">
                            <ClipboardList className="h-10 w-10 mx-auto text-[#C79520]/60 mb-3" />
                            No submissions match your filters.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="grading-table">
                                <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Student</th>
                                        <th className="text-left px-4 py-3 font-semibold">Chapter</th>
                                        <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                                        <th className="text-left px-4 py-3 font-semibold">Marks</th>
                                        <th className="text-right px-4 py-3 font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((s) => (
                                        <tr key={s.id} className="border-t border-border hover:bg-muted/20 transition" data-testid={`grading-row-${s.id}`}>
                                            <td className="px-4 py-3">
                                                <div className="font-medium">{s.student?.name || "—"}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {s.student?.email} · {s.student?.student_id}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                <div className="font-medium">{s.chapter?.title || s.chapter_id}</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs">{fmtDate(s.submitted_at)}</td>
                                            <td className="px-4 py-3 text-xs">
                                                <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_STYLES[s.status] || ""}`}>
                                                    {s.status}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                {s.marks !== null && s.marks !== undefined
                                                    ? <span className="font-mono font-semibold text-[#134B30]">{s.marks}</span>
                                                    : <span className="text-muted-foreground italic">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex gap-1.5">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => downloadSubmission(s)}
                                                        disabled={!s.file}
                                                        data-testid={`grading-download-${s.id}`}
                                                    >
                                                        <Download className="h-3 w-3 mr-1" />
                                                        File
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setGrading(s)}
                                                        className="bg-[#134B30] hover:bg-[#0E3A24] text-white gap-1"
                                                        data-testid={`grading-grade-${s.id}`}
                                                    >
                                                        <Star className="h-3 w-3" />
                                                        {s.status === "graded" ? "Re-grade" : "Grade"}
                                                        <ChevronRight className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <GradingDialog
                submission={grading}
                onClose={() => setGrading(null)}
                onSaved={() => { setGrading(null); load(); }}
            />
        </div>
    );
}


function GradingDialog({ submission, onClose, onSaved }) {
    const [marks, setMarks] = useState("");
    const [feedback, setFeedback] = useState("");
    const [saving, setSaving] = useState(false);
    const [assignment, setAssignment] = useState(null);

    useEffect(() => {
        if (!submission) {
            setMarks(""); setFeedback(""); setAssignment(null);
            return;
        }
        setMarks(submission.marks ?? "");
        setFeedback(submission.feedback ?? "");
        // Load assignment to get max_marks
        api.get(`/admin/chapters/${submission.chapter_id}/assignment`)
            .then(({ data }) => setAssignment(data?.assignment))
            .catch(() => setAssignment(null));
    }, [submission]);

    const save = async () => {
        if (!submission) return;
        const numMarks = Number(marks);
        if (isNaN(numMarks) || numMarks < 0) {
            toast.error("Marks must be a non-negative number.");
            return;
        }
        const max = assignment?.max_marks || 25;
        if (numMarks > max) {
            toast.error(`Marks cannot exceed ${max}.`);
            return;
        }
        setSaving(true);
        try {
            await api.post(
                `/admin/assignment-submissions/${submission.id}/grade`,
                { marks: numMarks, feedback },
            );
            toast.success("Grade saved");
            onSaved?.();
        } catch (e) {
            toast.error(fmtError(e, "Could not save grade"));
        } finally {
            setSaving(false);
        }
    };

    const download = async () => {
        if (!submission) return;
        try {
            const res = await api.get(
                `/admin/assignment-submissions/${submission.id}/download`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = submission.file?.filename || "submission";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(fmtError(e, "Could not download"));
        }
    };

    if (!submission) return null;
    const max = assignment?.max_marks || 25;

    return (
        <Dialog open={!!submission} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl" data-testid="grading-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-[#134B30]">
                        Grade submission
                    </DialogTitle>
                    <DialogDescription>
                        {submission.student?.name} — {submission.chapter?.title}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    {submission.file && (
                        <div className="flex items-center gap-2 p-3 bg-[#FBF5E8]/60 rounded border border-[#E1DAC5]">
                            <FileText className="h-4 w-4 text-[#134B30]" />
                            <span className="text-sm flex-1 truncate">{submission.file.filename}</span>
                            <Button size="sm" variant="outline" onClick={download}>
                                <Download className="h-3 w-3 mr-1" />
                                Download
                            </Button>
                        </div>
                    )}
                    <div>
                        <Label>Marks (out of {max})</Label>
                        <Input
                            type="number"
                            min={0}
                            max={max}
                            step="0.5"
                            value={marks}
                            onChange={(e) => setMarks(e.target.value)}
                            data-testid="grading-marks-input"
                        />
                    </div>
                    <div>
                        <Label>Feedback</Label>
                        <Textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            rows={5}
                            placeholder="Feedback shown to the student alongside their grade."
                            data-testid="grading-feedback-input"
                        />
                    </div>
                </div>
                <DialogFooter className="mt-4">
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-[#134B30] hover:bg-[#0E3A24] text-white gap-1.5"
                        data-testid="grading-save-btn"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Save grade
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
